/**
 * Single source of truth for the one-off ("prepay") checkout used by the DE and
 * NL markets, where a booking is paid up front instead of submitted as a
 * request. The CH equivalent is `useBloodCheckout`, which sells the yearly
 * membership subscription — the two never both apply (see `marketPrepays`).
 *
 * Everything is priced at the market's list price: no membership line, no
 * member pricing, no cadence. That's why this can't reuse
 * `computeBloodMembershipPricing`, which charges the baseline blood test at the
 * yearly membership price instead of its own.
 */
import { useBookingFlow } from "@/hooks/useBookingFlow";
import { useBookingStore } from "@/stores/bookingStore";
import { useEmbeddedPaymentStore } from "@/stores/embeddedPaymentStore";
import { voucherDiscountCents } from "@/utils/voucherDiscount";
import { oneOffSubtotalCents } from "@/utils/oneOffPricing";

export function useOneOffCheckout() {
  const { selectedProductDetails, selectedAddOnDetails, language } =
    useBookingFlow();
  const setEmbeddedPaymentOpen = useEmbeddedPaymentStore((s) => s.setOpen);
  const captureBookingAttempt = useEmbeddedPaymentStore(
    (s) => s.captureBookingAttempt,
  );
  const discount = useBookingStore((s) => s.discount);

  const cart = [...selectedProductDetails, ...selectedAddOnDetails];

  const subtotalCents = oneOffSubtotalCents(cart);
  // Displayed discount only. Stripe applies the real coupon to the invoice, so
  // the charged total can differ by rounding — which is exactly why the server
  // reconciles the SUBTOTAL (deterministic) and not this.
  const discountCents = voucherDiscountCents(discount, subtotalCents);
  const totalCents = subtotalCents - discountCents;

  // What the edge function needs to re-price each line from Stripe itself. The
  // metadata id is the mode-agnostic handle (live and sandbox use different
  // product ids for the same product).
  const items = cart
    .filter((p) => !!p.stripe_product_id)
    .map((p) => ({
      stripeProductId: p.stripe_product_id!,
      stripeMetadataId: p.stripe_metadata_id ?? undefined,
    }));

  const startCheckout = () => {
    // Edge-independent last resort BEFORE the popup: durably capture the
    // booking so the customer's data survives even if the edge function is
    // slow/down or a redirect method (Klarna/PayPal) never returns.
    captureBookingAttempt?.();
    setEmbeddedPaymentOpen(true);
  };

  return {
    startCheckout,
    cart,
    items,
    subtotalCents,
    discountCents,
    totalCents,
    language,
  };
}
